import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Twitter, Linkedin, Instagram, MapPin, Phone, Mail } from "lucide-react";
import { useData } from "../context/DataContext";

const Footer = () => {
  const { companyInfo, services } = useData();
  
  return (
    <>
      {/* Orange separator */}
      <div className="w-full h-2" style={{ backgroundColor: "var(--primary-orange)" }} />
      
      <footer className="gh-footer" data-testid="site-footer" style={{ backgroundColor: "var(--dark-navy)", color: "var(--white)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div>
              <img 
                src="/seo-images/groundhogs-logo.png" 
                alt="Groundhogs™ Environmental - Septic Solutions" 
                className="h-16 w-auto mb-4"
              />
              <p className="text-sm mb-4 opacity-80">
                Groundhogs™ provides septic installation services in Northwestern Ontario. 
                Permanent solutions backed by expert service.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="hover:opacity-70" aria-label="Facebook">
                  <Facebook size={20} />
                </a>
                <a href="#" className="hover:opacity-70" aria-label="Twitter">
                  <Twitter size={20} />
                </a>
                <a href="#" className="hover:opacity-70" aria-label="LinkedIn">
                  <Linkedin size={20} />
                </a>
                <a href="#" className="hover:opacity-70" aria-label="Instagram">
                  <Instagram size={20} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/" className="hover:opacity-70">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="hover:opacity-70">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/gallery" className="hover:opacity-70">
                    Project Gallery
                  </Link>
                </li>
                <li>
                  <Link to="/testimonials" className="hover:opacity-70">
                    Customer Reviews
                  </Link>
                </li>
                <li>
                  <Link to="/information-centre" className="hover:opacity-70">
                    Information Centre
                  </Link>
                </li>
                <li>
                  <Link to="/thunder-bay/septic-systems" className="hover:opacity-70">
                    Thunder Bay Septic Systems
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="hover:opacity-70">
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link to="/divisions" className="hover:opacity-70">
                    Divisions
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="hover:opacity-70">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* Our Services */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Our Services</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/services/septic-system-installation-thunder-bay" className="hover:opacity-70">
                    Septic System Installation in Thunder Bay
                  </Link>
                </li>
                <li>
                  <Link to="/services/septic-field-installation-replacement-thunder-bay" className="hover:opacity-70">
                    Septic Field Installation / Replacement Thunder Bay
                  </Link>
                </li>
                <li>
                  <Link to="/services/septic-tank-installation-thunder-bay" className="hover:opacity-70">
                    Septic Tank Installation &amp; Replacement (Thunder Bay)
                  </Link>
                </li>
                <li>
                  <Link to="/services/roth-multitank-supplier-thunder-bay" className="hover:opacity-70">
                    Roth MultiTank – Plastic Septic & Water Tanks
                  </Link>
                </li>
                <li>
                  <Link to="/services/septic-repair-near-me" className="hover:opacity-70">
                    Septic Repair Near Me
                  </Link>
                </li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/information-centre" className="hover:opacity-70">
                    Information Centre
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="hover:opacity-70">
                    Frequently Asked Questions
                  </Link>
                </li>
                <li>
                  <Link to="/testimonials" className="hover:opacity-70">
                    Customer Testimonials
                  </Link>
                </li>
                <li>
                  <Link to="/gallery" className="hover:opacity-70">
                    Project Gallery
                  </Link>
                </li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-6">Popular Service Areas</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/service-areas/thunder-bay" className="hover:opacity-70">
                    Thunder Bay
                  </Link>
                </li>
                <li>
                  <Link to="/service-areas/kenora" className="hover:opacity-70">
                    Kenora
                  </Link>
                </li>
                <li>
                  <Link to="/service-areas/dryden" className="hover:opacity-70">
                    Dryden
                  </Link>
                </li>
                <li>
                  <Link to="/service-areas/fort-frances" className="hover:opacity-70">
                    Fort Frances
                  </Link>
                </li>
                <li>
                  <Link to="/service-areas/sioux-lookout" className="hover:opacity-70">
                    Sioux Lookout
                  </Link>
                </li>
                <li>
                  <Link to="/service-areas" className="hover:opacity-70" style={{ color: "var(--primary-orange)" }}>
                    View All Areas →
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Us */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start space-x-2">
                  <MapPin size={18} className="mt-1 flex-shrink-0" style={{ color: "var(--primary-orange)" }} />
                  <div>
                    <div className="font-semibold">Main Headquarters</div>
                    <div className="opacity-80">
                      {companyInfo.address.street}<br />
                      {companyInfo.address.city}, {companyInfo.address.province}<br />
                      {companyInfo.address.postalCode}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone size={18} style={{ color: "var(--primary-orange)" }} />
                  <a href="tel:1-888-907-3777" className="hover:opacity-70 whitespace-nowrap">
                    1-888-907-3777
                  </a>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail size={18} style={{ color: "var(--primary-orange)" }} />
                  <a href={`mailto:${companyInfo.email}`} className="hover:opacity-70">
                    {companyInfo.email}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="mt-12 pt-8 border-t border-gray-700">
            <div className="flex flex-col md:flex-row justify-between items-center text-sm">
              <img 
                src="/seo-images/groundhogs-logo.png" 
                alt="Groundhogs™ Environmental" 
                className="h-12 w-auto mb-4 md:mb-0"
              />
              <div className="opacity-80 text-center md:text-left">
                Proudly serving Northwestern Ontario from Thunder Bay HQ
              </div>
            </div>
            <div className="text-center mt-4 opacity-80 text-sm">
              Groundhogs™ (CIPO #2435119) • <Link to="/divisions" className="hover:opacity-70" style={{ color: "var(--primary-orange)" }}>Divisions & Licensing</Link> • Licensed operators: <a href="mailto:hello@groundhogs.ca" className="hover:opacity-70" style={{ color: "var(--primary-orange)" }}>hello@groundhogs.ca</a>
            </div>
            <div className="text-center mt-2 opacity-60 text-xs">
              © 2025 Groundhogs™. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;